import React, { useCallback, useContext, useEffect, useRef } from 'react'
import BusinessTravellerAccountGuard from 'businessTraveller/components/BusinessTravellerAccountGuard'
import BusinessTravellerAccountMenuContent from 'businessTraveller/components/business-account/BusinessTravellerAccountMenuContent'
import ModalContext from 'contexts/ModalContext'
import { useAppFooterObserver } from 'hooks/useAppLayoutObservers'
import AppNavigationBar, { AppNavigationBarBackground } from 'LuxKit/AppNavigation/AppNavigationBar'
import AppNavigationTab from 'LuxKit/AppNavigation/AppNavigationTab'
import zIndex from 'styles/tools/z-index'
import { APP_BOTTOM_NAV_TABS, AppBottomNavTabId } from './appBottomNavTabs'
import { useAppDispatch } from 'hooks/reduxHooks'
import fetchAccountBusiness from 'actions/businessTraveller/fetchBusinessAccount'
import useSocieteNotificationFetcher from 'businessTraveller/hooks/useSocieteNotificationFetcher'
import SocieteNotificationModalTrigger from 'loyalty/pages/societeCampaign/SocieteNotification/SocieteNotificationModalTrigger'

interface Props {
  /** Current active tab (highlighted) */
  activeTab?: AppBottomNavTabId
  /** Background style of the nav bar: transparent or solid */
  background?: AppNavigationBarBackground
}

/**
 * Bottom tab bar for the white label app, fixed to the bottom of the viewport
 */
function AppBottomNav({ activeTab, background }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const showModal = useContext(ModalContext)
  const dispatch = useAppDispatch()
  useAppFooterObserver(ref, 'sticky')

  // Global header is not shown on mobile, hence need to add useEffect here to fetch the business account
  useEffect(() => {
    dispatch(fetchAccountBusiness())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch the societe details for the societe notifications and societe card in the account drawer
  const activeSocieteNotification = useSocieteNotificationFetcher()

  const openAccountMenu = useCallback(() => {
    showModal(<BusinessTravellerAccountMenuContent hideAppBottomNavItems />)
  }, [showModal])

  return <div
    ref={ref}
    style={{
      position: 'fixed',
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: zIndex.appBottomNav,
      pointerEvents: 'none',
    }}
  >
    <SocieteNotificationModalTrigger activeSocieteNotification={activeSocieteNotification} />
    <AppNavigationBar aria-label="Main" background={background} data-testid="app-bottom-nav">
      {APP_BOTTOM_NAV_TABS.map(tab => {
        const isActive = tab.id === activeTab
        const Icon = isActive ? tab.activeIcon : tab.icon
        const tabElement = <AppNavigationTab
          key={tab.id}
          to={tab.to}
          onClick={tab.action === 'account-menu' ? openAccountMenu : undefined}
          aria-haspopup={tab.action ? 'dialog' : undefined}
          isActive={isActive}
          icon={<Icon />}
          data-testid={`app-bottom-nav-tab-${tab.id}`}
        >
          {tab.label}
        </AppNavigationTab>

        if (tab.employeeRoles) {
          return <BusinessTravellerAccountGuard key={tab.id} employeeRoles={tab.employeeRoles}>
            {tabElement}
          </BusinessTravellerAccountGuard>
        }

        return tabElement
      })}
    </AppNavigationBar>
  </div>
}

export default AppBottomNav
